import React, { useEffect, useRef, useState } from "react"
import { Tooltip } from "react-bootstrap"

export const renderTooltip = (message: string) => (
    <Tooltip id='0'>
        {message}
    </Tooltip>
)

export const base64ToBlob = (base64: string, contentType = 'application/pdf') => {
    const byteCharacters = atob(base64)
    const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0))
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: contentType })
}

// Hook para detectar si esta en mobile
export const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768)

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768)
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return isMobile
}

// Hook para detectar si esta en una tablet
export const useIsTablet = () => {
    const [isXL, setIsXL] = useState(window.innerWidth >= 1200)

    useEffect(() => {
        const handleResize = () => setIsXL(window.innerWidth >= 1200)
        window.addEventListener("resize", handleResize)
        return () => window.removeEventListener("resize", handleResize)
    }, [])

    return isXL
}

//Hook para detectar si un componente se ha desmontado
export const useSafeAsync = () => {
    const isMounted = useRef(true)

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    function checkMounted() {
        return isMounted.current
    }

    return checkMounted
}

//Formateo números para pesos en un input 
export const parseNumeroRobusto = (valor: string | number) => {
    if (valor === null || valor === undefined) return 0
    let s = String(valor).trim()

    if (s === '') return 0

    const tienePunto = s.indexOf('.') !== -1
    const tieneComa = s.indexOf(',') !== -1

    if (tienePunto && tieneComa) {
        s = s.replace(/\./g, '').replace(',', '.')
    } else if (tieneComa && !tienePunto) {
        s = s.replace(',', '.')
    } else if (tienePunto && !tieneComa) {

        const partes = s.split('.')
        const parteDecimal = partes[partes.length - 1]
        if (parteDecimal.length === 0) {
            // eslint-disable-next-line        
            s = s
        } else if (parteDecimal.length <= 2) {
            // eslint-disable-next-line        
            s = s
        } else {
            s = s.replace(/\./g, '')
        }
    }

    const n = Number(s)
    return isNaN(n) ? 0 : n
}

export const codAreaPais = [
    { code: '+54', country: 'ARG' },
    { code: '+591', country: 'BOL' },
    { code: '+55', country: 'BRA' },
    { code: '+56', country: 'CHL' },
    { code: '+57', country: 'COL' },
    { code: '+593', country: 'ECU' },
    { code: '+595', country: 'PRY' },
    { code: '+51', country: 'PER' },
    { code: '+598', country: 'URY' },
    { code: '+58', country: 'VEN' }]

//regex para validar números de celular por país
export const regexPorPais = {
    '+54': /^[0-9]{10}$/,             // Argentina - SIEMPRE 10 dígitos
    '+591': /^[0-9]{8}$/,             // Bolivia - móviles 8 dígitos
    '+55': /^[0-9]{10,11}$/,          // Brasil - 10 o 11 dígitos
    '+56': /^[0-9]{9}$/,              // Chile - móviles 9 dígitos
    '+57': /^[0-9]{10}$/,             // Colombia - móviles 10 dígitos
    '+593': /^[0-9]{9}$/,             // Ecuador - móviles 9 dígitos
    '+595': /^[0-9]{9}$/,             // Paraguay - móviles 9 dígitos
    '+51': /^[0-9]{9}$/,              // Perú - móviles 9 dígitos
    '+598': /^[0-9]{8,9}$/,           // Uruguay - móviles 8 o 9 dígitos
    '+58': /^[0-9]{10,11}$/           // Venezuela - móviles 10 o 11 dígitos
} as const

export const isValidNumeric = (value: string) => {
    return !isNaN(Number(value)) && value.trim() !== "";
};

export const calcularDigitoCuit = (cuit: string) => {
    try {
        const mult = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];

        let total = 0;

        for (let i = 0; i < mult.length; i++) {
            total += parseInt(cuit[i], 10) * mult[i];
        }

        const resto = total % 11;

        if (resto === 0) return 0;
        if (resto === 1) return 9;

        return 11 - resto;
    } catch {
        return -1;
    }
};

export const isCuitValido = (cuit: string) => {
    if (!cuit) return false;

    cuit = cuit.replace(/-/g, "").replace(/ /g, "");

    if (cuit.length !== 11) return false;

    const calculado = calcularDigitoCuit(cuit);
    const digito = parseInt(cuit.substring(10), 10);

    return calculado === digito;
};

export const isValidDestinacion = (despacho: string) => {
    if (despacho.length !== 16) return false;

    const anio = despacho.substring(0, 2);
    const aduana = despacho.substring(2, 5);
    const numeroLegajo = despacho.substring(9, 15);

    if (
        !isValidNumeric(anio) ||
        !isValidNumeric(aduana) ||
        !isValidNumeric(numeroLegajo)
    ) {
        return false;
    }

    let destUc = despacho.toUpperCase();

    let isExpo = false;

    if (destUc.indexOf("NAME") === 5) {
        isExpo = true;
        destUc = destUc.replace("MANE", "MANI");
    }

    let suma = 0;

    for (let i = 0; i <= 14; i++) {
        suma += destUc.charCodeAt(i);
    }

    let mod23 = (suma % 23) + 65;

    if (isExpo) mod23++;

    switch (mod23) {
        case 73:
            mod23 = 88;
            break;
        case 79:
            mod23 = 89;
            break;
        case 81:
            mod23 = 90;
            break;
        case 91:
            mod23 = 65;
            break;
    }

    return mod23 === destUc.charCodeAt(15);
};